import React from 'react';
import KaAlert from '../Common/KaAlert'
import SpinnerButton from '../Common/SpinnerButton';
import { useDisplayMessage } from '../Common/useDisplayMessage';
import KaModal from '../Common/KaModal';
import { Path, useForm } from 'react-hook-form';
import { RowFormText } from '../Common/Forms/FormText';
import useUpsertPurchaseOrder, { PoUpsertDto } from '../Requests/PurchaseOrders/useUpsertPurchaseOrder';
import { PurchaseOrderDto } from '../Requests/PurchaseOrders/useGetPurchaseOrders';
import useGetSuppliers from '../Requests/Suppliers/useGetSuppliers';
import useGetSitesNonAdmin from '../Requests/useGetSitesNonAdmin';
import MultiQueryView from '../Common/MultiQueryView';
import useGetProducts from '../Requests/useGetProducts';
import { RowFormSelectSearch } from '../Common/Forms/SelectSearch';
import { RowUnitsSelect } from '../Units/UnitSelect';
import { RowHooksFormTextArea } from '../Common/Forms/FormTextArea';
import BoldButton from '../Common/BoldButton';

interface IProps {
  initPo?: PurchaseOrderDto,
  viewOnly: boolean,
  onHide: () => void,
  onSuccess: (supplier: PoUpsertDto) => void
}

export const CreateEditPurchaseOrder = (props: IProps) => {
  const displayMessage = useDisplayMessage();
  const getSites = useGetSitesNonAdmin();
  const getProducts = useGetProducts();
  const getSuppliers = useGetSuppliers();
  const NotAssignedId = "NotAssigned";

  const { register, handleSubmit, getValues, setValue, formState: { errors } } = useForm<PoUpsertDto>({
    defaultValues: {
      number: props.initPo?.number ?? '',
      supplierId: props.initPo?.supplierId ?? '',
      productId: props.initPo?.productId ?? '',
      purchased: props.initPo?.purchased ?? 0,
      unit: props.initPo?.unit ?? 'Pound',
      ticketNotes: props.initPo?.ticketNotes,
      siteId: props.initPo?.siteId ?? NotAssignedId,
    }
  });

  const useCreateEdit = useUpsertPurchaseOrder(props.initPo?.id, {
    onSuccess: () => props.onSuccess(getValues()),
    onError: (e) => displayMessage.fail(e.message),
  });

  const handleCreateUpdate = (poUpsert: PoUpsertDto) => {
    useCreateEdit.request({
      ...poUpsert,
      siteId: poUpsert.siteId === NotAssignedId ? undefined : poUpsert.siteId,
    });
  }

  const hookProps = (name: Path<PoUpsertDto>) => ({
    labelSpace: 4,
    inputSpace: 7,
    name: name,
    setValue,
    register,
    errors,
  });

  const onClose = () => {
    props.onHide();
    displayMessage.clear();
  }

  const titleAction = props.viewOnly
    ? 'View'
    : !props.initPo ? 'Create' : 'Update';

  return (<KaModal
    onHide={onClose}
    show={true}
    title={`${titleAction} a purchase order`}
    body={(<>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <MultiQueryView
        queries={{ sites: getSites, products: getProducts, suppliers: getSuppliers }}
        renderData={({ sites, products, suppliers }) => <>
          <RowFormText
            {...hookProps("number")}
            enabled={!props.viewOnly}
            options={{
              required: 'Number is required',
              validate: {
                length: v => v.length < 51 || 'Number cannot be longer than 50 characters'
              }
            }} />
          <RowFormSelectSearch
            {...hookProps('productId')}
            id={'productId'}
            displayName="Product"
            selectedValue={getValues('productId')}
            placeholder="Select product"
            disabled={props.viewOnly}
            selectOptions={products
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(p => ({ value: p.id, name: p.name }))}
            options={{ required: 'A product must be selected' }} />
          <RowFormSelectSearch
            {...hookProps('supplierId')}
            id={'supplierId'}
            displayName="Supplier"
            selectedValue={getValues('supplierId')}
            placeholder="Select supplier"
            disabled={props.viewOnly}
            selectOptions={suppliers
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(s => ({ value: s.id, name: s.name }))}
            options={{ required: 'A supplier must be selected' }} />
          <RowFormSelectSearch
            {...hookProps('siteId')}
            id={'siteId'}
            displayName="Site"
            selectedValue={getValues('siteId') ?? NotAssignedId}
            hideSearch={true}
            placeholder="Select a site"
            disabled={props.viewOnly}
            selectOptions={[
              { value: NotAssignedId, name: 'Not Assigned to a site...' },
              ...sites
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(s => ({ value: s.id, name: s.name }))
            ]} />
          <RowFormText
            {...hookProps("purchased")}
            enabled={!props.viewOnly}
            options={{
              valueAsNumber: true,
              validate: {
                number: v => !isNaN(Number(v)) || 'Must be a number',
                greaterThanZero: v => 0 < v || 'Must be more than 0',
                lessThanMax: v => v < 100000000 || 'Must be less than 100,000,000',
              }
            }} />
          <RowUnitsSelect
            {...hookProps('unit')}
            id='unit'
            displayName='Purchased Unit'
            disabled={props.viewOnly}
            selectedValue={getValues("unit")} />
          <RowHooksFormTextArea {...hookProps("ticketNotes")} />
        </>
        } />
    </>)}
    footer={
      !props.viewOnly ?
        <SpinnerButton
          className="btn-ghost-primary"
          spinning={useCreateEdit.isLoading}
          onClick={handleSubmit(handleCreateUpdate)}>
          {`${!props.initPo ? 'Create' : 'Update'}`}
        </SpinnerButton>
        : <BoldButton className='col-3 btn-ghost-secondary' onClick={props.onHide}>
          Close
        </BoldButton>
    }
  />);
}
